import apiClient from './apiService';
import { Location } from '../components/location/LocationTypes';
import { Party } from '../components/party/PartyTypes';
import { Authority } from '../components/authority/AuthorityTypes';

// --- Locations ---

export const getLocations = async (page = 1, limit = 20) => {
    const response = await apiClient.get<{ data: Location[], meta: any }>(`/api/v1/org/locations?page=${page}&limit=${limit}`);
    return response.data;
};

export const getLocation = async (id: string | number) => {
    const response = await apiClient.get<Location>(`/api/v1/org/locations/${id}`);
    return response.data;
};

export const createLocation = async (data: Omit<Location, 'id'>) => {
    const response = await apiClient.post<Location>('/api/v1/org/locations', data);
    return response.data;
};

export const updateLocation = async (id: string | number, data: Partial<Location>) => {
    const response = await apiClient.put<Location>(`/api/v1/org/locations/${id}`, data);
    return response.data;
};

export const deleteLocation = async (id: string | number) => {
    await apiClient.delete(`/api/v1/org/locations/${id}`);
};

export const submitLocation = (id: string | number): Promise<void> =>
    apiClient.post(`/api/v1/org/locations/${id}/submit`).then(() => undefined);

export const approveLocation = (id: string | number, note?: string): Promise<void> =>
    apiClient.post(`/api/v1/org/locations/${id}/approve`, { note }).then(() => undefined);

export const rejectLocation = (id: string | number, reason: string): Promise<void> =>
    apiClient.post(`/api/v1/org/locations/${id}/reject`, { reason }).then(() => undefined);

export const reopenLocation = (id: string | number): Promise<void> =>
    apiClient.post(`/api/v1/org/locations/${id}/reopen`).then(() => undefined);

export const getLocationHistory = async (
    id: number
): Promise<import('../components/common/AuditHistoryModal').AuditRecord[]> => {
    const response = await apiClient.get<{ items: any[] }>(`/api/v1/org/locations/${id}/history`);
    const rows = response.data.items ?? (response.data as any) ?? [];
    return rows.map((r: any) => ({
        audit_id: r.id,
        entity_id: r.entity_id,
        action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
        changed_by: r.changed_by,
        changed_by_username: r.changed_by_username || 'Unknown',
        changed_on: r.changed_on,
        change_reason: r.remarks,
    }));
};

// --- Parties ---

export const getParties = async (page = 1, limit = 20) => {
    const response = await apiClient.get<{ data: Party[], meta: any }>(`/api/v1/org/parties?page=${page}&limit=${limit}`);
    return response.data;
};

export const getParty = async (id: string | number) => {
    const response = await apiClient.get<Party>(`/api/v1/org/parties/${id}`);
    return response.data;
};

export const createParty = async (data: Omit<Party, 'id'>) => {
    const response = await apiClient.post<Party>('/api/v1/org/parties', data);
    return response.data;
};

export const updateParty = async (id: string | number, data: Partial<Party>) => {
    const response = await apiClient.put<Party>(`/api/v1/org/parties/${id}`, data);
    return response.data;
};

export const deleteParty = async (id: string | number) => {
    await apiClient.delete(`/api/v1/org/parties/${id}`);
};

// --- Profile ---

export const getProfile = async () => {
    const response = await apiClient.get<any>('/api/v1/org/profile');
    return response.data;
};

export const updateProfile = async (data: any) => {
    const response = await apiClient.put<any>('/api/v1/org/profile', data);
    return response.data;
};

// --- Entity Settings (per-entity display styling; singleton, edit-in-place) ---

export interface EntitySettings {
    id: number;
    entity_id: number;
    currency_code: string;
    currency_symbol: string;
    number_format: 'INDIAN' | 'INTERNATIONAL';
    decimal_places: number;
    date_format: 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD';
    default_language: string;
    is_active: number;
}

export const getEntitySettings = async (): Promise<EntitySettings> => {
    const response = await apiClient.get<EntitySettings>('/api/v1/org/settings');
    return response.data;
};

export const updateEntitySettings = async (
    data: Partial<Omit<EntitySettings, 'id' | 'entity_id' | 'is_active'>>,
): Promise<EntitySettings> => {
    const response = await apiClient.put<EntitySettings>('/api/v1/org/settings', data);
    return response.data;
};

// --- Coding Prefix Configuration ---

export interface CodePrefixRow {
    type_code: string;
    display_name: string;
    module_code: string;
    system_default_prefix: string;
    effective_prefix: string;
    is_customised: boolean;
    has_codes_generated: boolean;
    code_column_exists: boolean;
}

export const getCodePrefixes = async (): Promise<CodePrefixRow[]> => {
    const response = await apiClient.get<CodePrefixRow[]>('/api/v1/org/settings/code-prefixes');
    return response.data;
};

export const saveCodePrefixes = async (
    prefixes: Array<{ type_code: string; prefix: string }>,
): Promise<CodePrefixRow[]> => {
    const response = await apiClient.put<CodePrefixRow[]>('/api/v1/org/settings/code-prefixes', { prefixes });
    return response.data;
};

export const revertCodePrefix = async (type_code: string): Promise<CodePrefixRow> => {
    const response = await apiClient.delete<CodePrefixRow>(`/api/v1/org/settings/code-prefixes/${type_code}`);
    return response.data;
};

export const revertAllCodePrefixes = async (): Promise<CodePrefixRow[]> => {
    const response = await apiClient.delete<CodePrefixRow[]>('/api/v1/org/settings/code-prefixes');
    return response.data;
};

// --- Entity GST ---

export const getEntityGst = async () => {
    const response = await apiClient.get<any[]>('/api/v1/org/profile/gst');
    return response.data;
};

export const createEntityGst = async (data: any) => {
    const response = await apiClient.post<any>('/api/v1/org/profile/gst', data);
    return response.data;
};

export const updateEntityGst = async (id: number, data: any) => {
    const response = await apiClient.put<any>(`/api/v1/org/profile/gst/${id}`, data);
    return response.data;
};

export const deleteEntityGst = async (id: number) => {
    await apiClient.delete(`/api/v1/org/profile/gst/${id}`);
};

export const submitEntityGst = (id: number): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/gst/${id}/submit`).then(() => undefined);
export const approveEntityGst = (id: number, note?: string): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/gst/${id}/approve`, { note }).then(() => undefined);
export const rejectEntityGst = (id: number, reason: string): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/gst/${id}/reject`, { reason }).then(() => undefined);
export const reopenEntityGst = (id: number): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/gst/${id}/reopen`).then(() => undefined);

// --- Entity TDS ---

export const getEntityTds = async () => {
    const response = await apiClient.get<any[]>('/api/v1/org/profile/tds');
    return response.data;
};

export const createEntityTds = async (data: any) => {
    const response = await apiClient.post<any>('/api/v1/org/profile/tds', data);
    return response.data;
};

export const updateEntityTds = async (id: number, data: any) => {
    const response = await apiClient.put<any>(`/api/v1/org/profile/tds/${id}`, data);
    return response.data;
};

export const deleteEntityTds = async (id: number) => {
    await apiClient.delete(`/api/v1/org/profile/tds/${id}`);
};

export const submitEntityTds = (id: number): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/tds/${id}/submit`).then(() => undefined);
export const approveEntityTds = (id: number, note?: string): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/tds/${id}/approve`, { note }).then(() => undefined);
export const rejectEntityTds = (id: number, reason: string): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/tds/${id}/reject`, { reason }).then(() => undefined);
export const reopenEntityTds = (id: number): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/tds/${id}/reopen`).then(() => undefined);

// --- Entity Registrations ---

export const getEntityRegistrations = async () => {
    const response = await apiClient.get<any[]>('/api/v1/org/profile/registrations');
    return response.data;
};

export const createEntityRegistration = async (data: any) => {
    const response = await apiClient.post<any>('/api/v1/org/profile/registrations', data);
    return response.data;
};

export const updateEntityRegistration = async (id: number, data: any) => {
    const response = await apiClient.put<any>(`/api/v1/org/profile/registrations/${id}`, data);
    return response.data;
};

export const deleteEntityRegistration = async (id: number) => {
    await apiClient.delete(`/api/v1/org/profile/registrations/${id}`);
};

export const submitEntityRegistration = (id: number): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/registrations/${id}/submit`).then(() => undefined);
export const approveEntityRegistration = (id: number, note?: string): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/registrations/${id}/approve`, { note }).then(() => undefined);
export const rejectEntityRegistration = (id: number, reason: string): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/registrations/${id}/reject`, { reason }).then(() => undefined);
export const reopenEntityRegistration = (id: number): Promise<void> =>
    apiClient.post(`/api/v1/org/profile/registrations/${id}/reopen`).then(() => undefined);

// --- Authorities ---

export const getAuthorities = async (page = 1, limit = 20) => {
    const response = await apiClient.get<{ data: Authority[], meta: any }>(`/api/v1/org/authorities?page=${page}&limit=${limit}`);
    return response.data;
};

export const getAuthority = async (id: string | number) => {
    const response = await apiClient.get<Authority>(`/api/v1/org/authorities/${id}`);
    return response.data;
};

export const createAuthority = async (data: Omit<Authority, 'id'>) => {
    const response = await apiClient.post<Authority>('/api/v1/org/authorities', data);
    return response.data;
};

export const updateAuthority = async (id: string | number, data: Partial<Authority>) => {
    const response = await apiClient.put<Authority>(`/api/v1/org/authorities/${id}`, data);
    return response.data;
};

export const deleteAuthority = async (id: string | number) => {
    await apiClient.delete(`/api/v1/org/authorities/${id}`);
};

export const submitAuthority = async (id: number): Promise<void> => {
    await apiClient.post(`/api/v1/org/authorities/${id}/submit`);
};

export const approveAuthority = async (id: number, note?: string): Promise<void> => {
    await apiClient.post(`/api/v1/org/authorities/${id}/approve`, { note });
};

export const rejectAuthority = async (id: number, reason: string): Promise<void> => {
    await apiClient.post(`/api/v1/org/authorities/${id}/reject`, { reason });
};

export const reopenAuthority = async (id: number): Promise<void> => {
    await apiClient.post(`/api/v1/org/authorities/${id}/reopen`);
};

export const getAuthorityHistory = async (
    id: number
): Promise<import('../components/common/AuditHistoryModal').AuditRecord[]> => {
    const response = await apiClient.get<{ items: any[] }>(`/api/v1/org/authorities/${id}/history`);
    const rows = response.data.items ?? (response.data as any) ?? [];
    return rows.map((r: any) => ({
        audit_id: r.id,
        entity_id: r.entity_id,
        action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
        changed_by: r.changed_by,
        changed_by_username: r.changed_by_username || 'Unknown',
        changed_on: r.changed_on,
        change_reason: r.remarks,
    }));
};

export const orgService = {
    getLocations,
    getLocation,
    createLocation,
    updateLocation,
    deleteLocation,
    getParties,
    getParty,
    createParty,
    updateParty,
    deleteParty,
    getProfile,
    updateProfile,
    getEntityGst,
    createEntityGst,
    updateEntityGst,
    deleteEntityGst,
    getEntityTds,
    createEntityTds,
    updateEntityTds,
    deleteEntityTds,
    getEntityRegistrations,
    createEntityRegistration,
    updateEntityRegistration,
    deleteEntityRegistration,
    submitEntityGst,
    approveEntityGst,
    rejectEntityGst,
    reopenEntityGst,
    submitEntityTds,
    approveEntityTds,
    rejectEntityTds,
    reopenEntityTds,
    submitEntityRegistration,
    approveEntityRegistration,
    rejectEntityRegistration,
    reopenEntityRegistration,
    getAuthorities,
    getAuthority,
    createAuthority,
    updateAuthority,
    deleteAuthority,
    submitAuthority,
    approveAuthority,
    rejectAuthority,
    reopenAuthority,
};
